/**
 * useSocket - React Hook para WebSocket Connection
 * 
 * Integra SocketService con React lifecycle.
 * Auto-conecta en mount, limpia en unmount.
 * 
 * @see src/services/socketService.js - Servicio singleton que consume
 * @requires AuthContext - Token JWT via useAuth()
 * 
 * @returns {{socket: object|null, connected: boolean, error: string|null}}
 * 
 * @example
 * function GameComponent() {
 *   const { socket, connected, error } = useSocket();
 *   
 *   useEffect(() => {
 *     if (connected && socket) {
 *       socket.on('room_created', (data) => {
 *         console.log('Room created:', data);
 *       });
 *     }
 *   }, [connected, socket]);
 *   
 *   return <div>Connected: {connected ? 'Yes' : 'No'}</div>;
 * }
 */

import { useState, useEffect } from 'react';
import SocketService from '../services/socketService';
import { useAuth } from '../context/AuthContext';

function useSocket() {
    const [socket, setSocket] = useState(null);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const { user, token } = useAuth(); // Integrate Auth Context

    useEffect(() => {
        // Get user ID for logging from Auth Context or localStorage fallback
        const userId = user?.user_id || localStorage.getItem('userId') || 'UNKNOWN';

        if (!token) {
            setError('No authentication token found');
            console.error('[useSocket] No auth token in localStorage');
            return;
        }

        // Sync localStorage if we have a valid user from context (Legacy Compatibility)
        if (user?.user_id) {
            if (localStorage.getItem('userId') !== String(user.user_id)) {
                console.log(`[useSocket] 🔄 Syncing localStorage userId: ${user.user_id}`);
                localStorage.setItem('userId', user.user_id);
            }
        }

        // Get SocketService instance
        const socketService = SocketService.getInstance();

        // If socket is already connected and managed by the service, use it
        if (socketService.isConnected() && socketService.getSocket()) {
            console.log(`[useSocket][U${userId}] 🔌 Socket already connected, using existing instance.`);
            setSocket(socketService.getSocket());
            setConnected(true);
            return; // Skip re-initialization
        }

        console.log(`[useSocket][U${userId}] 🔧 Initializing socket connection...`);
        // Connect to Games API
        const socketInstance = socketService.connect(token);
        setSocket(socketInstance);

        // Track heartbeat interval
        let heartbeatInterval = null;

        // Event handlers for connection state
        const handleConnect = () => {
            console.log(`[useSocket][U${userId}] ✅ Connected - SID: ${socketInstance.id}`);
            console.log(`[useSocket][U${userId}] 📊 Socket state:`, {
                connected: socketInstance.connected,
                disconnected: socketInstance.disconnected,
                id: socketInstance.id
            });
            setConnected(true);
            setError(null);

            // Start heartbeat
            console.log(`[useSocket][U${userId}] ❤️ Starting heartbeat interval (5 min)`);
            heartbeatInterval = setInterval(() => {
                if (socketInstance.connected) {
                    console.log(`[useSocket][U${userId}] 💓 Heartbeat sent`);
                    socketInstance.emit('heartbeat');
                } else {
                    console.warn(`[useSocket][U${userId}] ⚠️ Heartbeat skipped - socket disconnected`);
                }
            }, 300000); // 5 minutes
        };

        const handleDisconnect = (reason) => {
            console.log(`[useSocket][U${userId}] 🔌 Disconnected - Reason: ${reason}`);
            setConnected(false);
            if (heartbeatInterval) {
                console.log(`[useSocket][U${userId}] ❤️ Clearing heartbeat interval`);
                clearInterval(heartbeatInterval);
                heartbeatInterval = null;
            }

            // Only set error for abnormal disconnections
            if (reason === 'io server disconnect' || reason === 'io client disconnect') {
                // Normal disconnect, no error
            } else {
                setError(`Disconnected: ${reason}`);
            }
        };

        const handleConnectError = (err) => {
            console.error(`[useSocket][U${userId}] ❌ Connection error:`, err);
            setConnected(false);
            setError(err.message || 'Connection error');
        };

        const handleServerMessage = (data) => {
            if (data && data.user_id) {
                console.log(`[useSocket] 🆔 Received identity from server: ${data.user_id}`);
                localStorage.setItem('userId', data.user_id);
            }
        };

        // Subscribe to connection events
        socketService.on('connect', handleConnect);
        socketService.on('disconnect', handleDisconnect);
        socketService.on('connect_error', handleConnectError);
        socketService.on('server_message', handleServerMessage);

        // Check if already connected
        if (socketService.isConnected()) {
            setConnected(true);
        }

        // Cleanup on unmount
        return () => {
            console.log('[useSocket] Cleaning up event listeners');
            socketService.off('connect', handleConnect);
            socketService.off('disconnect', handleDisconnect);
            socketService.off('connect_error', handleConnectError);
            socketService.off('server_message', handleServerMessage);

            if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
            }
        };
    }, [user, token]); // Re-run if user auth state changes

    // Heartbeat interval to refresh presence TTL (5 minutes)
    useEffect(() => {
        if (!connected || !socket) return;

        // Only send heartbeat if we have a valid user
        if (!user?.user_id) return;

        console.log('[useSocket] Starting heartbeat interval (5 min)');

        const heartbeatInterval = setInterval(() => {
            socket.emit('heartbeat', (ack) => {
                if (ack && ack.success) {
                    console.log('[useSocket] ✅ Heartbeat ACK:', ack.timestamp);
                } else {
                    console.warn('[useSocket] ⚠️ Heartbeat failed:', ack);
                }
            });
        }, 5 * 60 * 1000); // 300,000ms = 5 minutes

        return () => {
            // Cleanup
            clearInterval(heartbeatInterval);
        };
    }, [connected, socket, user]);

    return { socket, connected, error };
}

export default useSocket;
